import Button from "components/button";
import ButtonIcon from "components/button-icon";
import Loading from "components/center-loading";
import { Chart } from "components/chart";
import DateInput from "components/date-input";
import LastUpdate from "components/last-update";
import TitleSecond from "components/title-second";
import { useGlobalContext } from "hooks/useGlobalContext";
import IconArrow from "images/arrow.svg";
import IconFilterGray from "images/icn-filter-gray.svg";
import moment from "moment";
import PropTypes from "prop-types";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Col, Grid, Row } from "react-flexbox-grid";
import intl from "react-intl-universal";
import { compareDates } from "utils/date";
import { getFeatureFlagsByKeys } from "utils/feature-flags";
import { DATA_FORMAT_TYPES, getDataFormat, ISO_DATE_FORMAT } from "utils/format-date";
import {
  fetchConsigneeStockCurve,
  searchChartData,
} from "../../pages/stocks-detail/stocks-detail.service";
import { formatErrorMessage } from "../../utils/handle-error";
import InventoryPolicyBoxes from "./components/inventory-policy-boxes";
import * as S from "./styled";
import { loadFilter, mountEchartData } from "./utils";

const ChartStocks = forwardRef(
  (
    {
      lastUpdateDate,
      idFamiliaRotuladaCentro,
      hideLastUpdate,
      hideLoading,
      sendInfosToSecondChart,
      updateChart,
      useStorageLocations,
      selectedStorageLocationId,
      showConsigneeStockCurve,
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = useState(false);
    const [filtroGrafico, setFiltroGrafico] = useState(loadFilter());
    const { showFeedback } = useGlobalContext();
    const [echart, setEchart] = useState(null);
    const [dateChart, setDateChart] = useState(filtroGrafico.dataDe);
    const [isHourlyPlan, setIsHourlyPlan] = useState(false);
    const [isDoiDynamicPolicyEnabled, setIsDoiDynamicPolicyEnabled] = useState(false);
    const [policyData, setPolicyData] = useState(null);
    const loadChartCallId = useRef(0);

    const changeValueFilter = (name, value) => {
      const dateGreaterThanToday = compareDates(value, filtroGrafico.dataDe);
      if ((name === "dataDe" && !value) || !dateGreaterThanToday) {
        return;
      }

      setFiltroGrafico((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    };

    const handleFilter = () => {
      setDateChart(filtroGrafico.dataDe);
      localStorage.setItem("filterChart", JSON.stringify(filtroGrafico));
      loadChart();
    };

    const loadChart = async () => {
      const callId = ++loadChartCallId.current;
      setIsLoading(true);
      try {
        let model = {
          DataDe: filtroGrafico.dataDe,
          DataAte: filtroGrafico.dataAte,
          DataEspecifica: dateChart,
        };

        if (useStorageLocations && selectedStorageLocationId)
          model = { ...model, BusinessUnitStorageLocationId: selectedStorageLocationId };

        const _chartData = await searchChartData(idFamiliaRotuladaCentro, model);

        if (callId !== loadChartCallId.current) return;

        setIsHourlyPlan(_chartData.IsHourlyPlan);
        const _policy = _chartData.DoiDynamicInventoryPolicyData;
        const doiFloorValue =
          isDoiDynamicPolicyEnabled && _policy?.Floor != null
            ? _policy.Floor
            : null;
        setEchart(
          mountEchartData(_chartData, dateChart, doiFloorValue)
        );
        setPolicyData(
          _policy
            ? {
                currentStock: _policy.CurrentStock,
                currentStockUnit: _policy.CurrentStockUnit,
                currentStockDoi: _policy.CurrentStockDoi,
                targetPolicy: _policy.TargetPolicy,
                targetPolicyUnit: _policy.TargetPolicyUnit,
                targetPolicyDoi: _policy.TargetPolicyDoi,
                minimumStock: _policy.MinimumStock,
                maximumStock: _policy.MaximumStock,
                minimumStockDoi: _policy.MinimumStockDoi,
                maximumStockDoi: _policy.MaximumStockDoi,
                policyStatus: _policy.PolicyStatus,
              }
            : null
        );

        if (showConsigneeStockCurve && !_chartData.IsHourlyPlan) {
          fetchConsigneeStockCurve(
            idFamiliaRotuladaCentro,
            filtroGrafico.dataDe,
            filtroGrafico.dataAte
          )
            .then((consigneeStockCurve) => {
              if (callId !== loadChartCallId.current) return;

              setEchart(
                mountEchartData(
                  _chartData,
                  dateChart,
                  doiFloorValue,
                  consigneeStockCurve
                )
              );
            })
            .catch(() => {
              // The consignee curve is supplementary and must not block the current chart.
            });
        }
      } catch (e) {
        if (callId !== loadChartCallId.current) return;
        showFeedback(formatErrorMessage(e));
      }
      setIsLoading(false);
    };

    const changeDayFilterNext = () => {
      setDateChart(moment(dateChart).add(1, "days").format(ISO_DATE_FORMAT));
    };

    const changeDayFilterPrevious = () => {
      setDateChart(moment(dateChart).subtract(1, "days").format(ISO_DATE_FORMAT));
    };

    useEffect(() => {
      if (sendInfosToSecondChart) {
        sendInfosToSecondChart(filtroGrafico);
      }
    }, [filtroGrafico]);

    useImperativeHandle(ref, () => ({
      async loadChart() {
        await loadChart();
      },
    }));

    useEffect(() => {
      loadChart();
    }, [updateChart, dateChart, selectedStorageLocationId, isDoiDynamicPolicyEnabled]);

    useEffect(() => {
      const checkFeatureFlag = async () => {
        const flags = await getFeatureFlagsByKeys(["FeatureDoiDynamicInventoryPolicy"]);
        setIsDoiDynamicPolicyEnabled(flags.FeatureDoiDynamicInventoryPolicy);
      };
      checkFeatureFlag();
    }, []);

    return (
      <S.Wrapper>
        <Grid fluid>
          <Row className="chart-title-row">
            <Col xs={4}>
              <TitleSecond>{intl.get("commons.stock")}</TitleSecond>
            </Col>
            <Col xs={8}>
              {!hideLastUpdate && (
                <LastUpdate
                  title={intl.get("stocks.lastUpdate")}
                  dataText={moment(lastUpdateDate, "DD/MM HH:mm").format("[DM] LT")}
                />
              )}
            </Col>
          </Row>
          <Row bottom="xs row-filter">
            <Col xs={4}>
              <DateInput
                label={intl.get("stocks.from")}
                value={filtroGrafico.dataDe}
                minDate={moment().format(ISO_DATE_FORMAT)}
                onChange={(date) => changeValueFilter("dataDe", date)}
                maxDate="9999-12-31"
              />
            </Col>
            <Col xs={4}>
              <DateInput
                label={intl.get("stocks.until")}
                value={filtroGrafico.dataAte}
                onChange={(date) => changeValueFilter("dataAte", date)}
                minDate={moment().format(ISO_DATE_FORMAT)}
                maxDate="9999-12-31"
              />
            </Col>
            <Col xs={4}>
              <Button
                value={intl.get("filters.filter")}
                buttonType="secondary"
                onClick={handleFilter}
                icon={IconFilterGray}
              />
            </Col>
          </Row>
          {isDoiDynamicPolicyEnabled && policyData && <InventoryPolicyBoxes data={policyData} />}
          {isHourlyPlan && (
            <S.SelectDatesContent>
              <ButtonIcon
                className="select-date-button-previous"
                buttonIcon={<img src={IconArrow} alt="icon-arrow" />}
                buttonType="hidden"
                disabled={dateChart <= filtroGrafico.dataDe}
                onClick={changeDayFilterPrevious}
              />
              {getDataFormat(dateChart, DATA_FORMAT_TYPES.WITH_YEAR)}
              <ButtonIcon
                className="select-date-button-next"
                buttonIcon={<img src={IconArrow} alt="icon-arrow" />}
                buttonType="hidden"
                disabled={dateChart >= filtroGrafico.dataAte}
                onClick={changeDayFilterNext}
              />
            </S.SelectDatesContent>
          )}

          <Row>
            <Col xs={12}>
              <Chart
                echart={echart}
                style={{ bottom: "30px", height: "400px" }}
                notMerge={true}
                lazyUpdate={true}
              >
                <S.LegendHint>
                  {intl.get("stocks.stockDetail.legendInteractionHint")}
                </S.LegendHint>
              </Chart>
              <Loading isLoading={isLoading && !hideLoading} fullHeightParent />
            </Col>
          </Row>
        </Grid>
      </S.Wrapper>
    );
  }
);

ChartStocks.propTypes = {
  lastUpdateDate: PropTypes.object,
  idFamiliaRotuladaCentro: PropTypes.number,
  hideLastUpdate: PropTypes.bool,
  hideLoading: PropTypes.bool,
  sendInfosToSecondChart: PropTypes.func,
  updateChart: PropTypes.bool,
  useStorageLocations: PropTypes.bool,
  selectedStorageLocationId: PropTypes.number,
  showConsigneeStockCurve: PropTypes.bool,
};

export default ChartStocks;
